import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  MegaphoneIcon, SpinnerGapIcon, XLogoIcon, WarningIcon, CheckCircleIcon,
  SkullIcon, CopyIcon, CaretDownIcon,
} from "@phosphor-icons/react";
import { hypePreview, hypeJob, hypePost, fetchHypeHistory, fetchWaitingRoom } from "../lib/api";
import { fmtUsd } from "../lib/format";

const FLAG_STYLE = {
  danger: { icon: SkullIcon, cls: "text-danger border-danger/40 bg-danger/[0.06]" },
  warn: { icon: WarningIcon, cls: "text-warn border-warn/40 bg-warn/[0.06]" },
  ok: { icon: CheckCircleIcon, cls: "text-neon border-neon/30 bg-neon/[0.05]" },
};

export const HypeMachine = () => {
  const qc = useQueryClient();
  const [mint, setMint] = useState("");
  const [busy, setBusy] = useState(false);
  const [posting, setPosting] = useState(null);
  const [preview, setPreview] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const waitersQ = useQuery({ queryKey: ["waitingRoom"], queryFn: fetchWaitingRoom, staleTime: 60000 });
  const historyQ = useQuery({ queryKey: ["hypeHistory"], queryFn: fetchHypeHistory });

  const generate = async (ca) => {
    const target = (ca || mint).trim();
    if (!target) { toast.error("Paste a CA or pick a Waiting Room coin"); return; }
    setBusy(true); setPreview(null);
    try {
      const job = await hypePreview(target);
      // poll the background job — prod proxies kill long requests, so generation runs server-side
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const res = await hypeJob(job.id);
        if (res.status === "done") {
          setPreview(res);
          setDrafts(res.variants);
          setMint(target);
          setBusy(false);
          return;
        }
        if (res.status === "failed") throw { response: { data: { detail: res.error || "Generation failed" } } };
      }
      throw { response: { data: { detail: "Timed out — try again" } } };
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't generate — try again"); }
    setBusy(false);
  };

  const post = async (i) => {
    const text = drafts[i];
    if (!window.confirm(`Post this to X as @AlphaApingrules?\n\n${text}`)) return;
    setPosting(i);
    try {
      const res = await hypePost(preview.coin.mint, text);
      toast.success("Hype posted to X 🎯", { action: { label: "Open", onClick: () => window.open(res.url, "_blank") }, duration: 12000 });
      qc.invalidateQueries({ queryKey: ["hypeHistory"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Post failed"); }
    setPosting(null);
  };

  const waiters = (waitersQ.data?.waiting || []).slice(0, 15);
  const history = historyQ.data?.posts || [];

  return (
    <div className="border border-[#F0C000]/25 bg-[#F0C000]/[0.03] p-5 space-y-4" data-testid="hype-machine">
      <div className="flex items-center gap-2">
        <MegaphoneIcon size={20} weight="fill" className="text-[#F0C000]" />
        <h3 className="font-heading font-bold text-base">Hype Machine</h3>
        <span className="text-[10px] font-mono uppercase text-muted-foreground">call a coin like a KOL — safety-checked first</span>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          data-testid="hype-mint-input"
          value={mint}
          onChange={(e) => setMint(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && generate()}
          placeholder="Paste a contract address (CA)…"
          className="flex-1 min-w-[260px] bg-black/40 border border-white/15 px-3 py-2 text-sm font-mono focus:border-[#F0C000]/60 outline-none"
        />
        <div className="relative">
          <select
            data-testid="hype-waiter-select"
            onChange={(e) => e.target.value && generate(e.target.value)}
            value=""
            className="appearance-none bg-black/40 border border-white/15 pl-3 pr-8 py-2 text-sm font-mono text-muted-foreground focus:border-[#F0C000]/60 outline-none cursor-pointer"
          >
            <option value="">…or pick from Waiting Room</option>
            {waiters.map((w) => (
              <option key={w.mint} value={w.mint}>${(w.symbol || "?").toUpperCase()} · score {w.waiterScore} · {fmtUsd(w.lastMcap)}</option>
            ))}
          </select>
          <CaretDownIcon size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>
        <button data-testid="hype-generate-btn" onClick={() => generate()} disabled={busy}
          className="px-4 py-2 bg-[#F0C000] text-black text-xs font-bold hover:bg-[#F0C000]/90 transition-colors disabled:opacity-50 flex items-center gap-1.5">
          {busy ? <SpinnerGapIcon size={14} className="animate-spin" /> : <MegaphoneIcon size={14} weight="fill" />}
          {busy ? "Scouting + writing…" : "Generate hype"}
        </button>
      </div>

      {preview && (
        <div className="space-y-4 animate-fade-up">
          <div className="flex items-center gap-3 flex-wrap">
            {preview.coin.image && <img src={preview.coin.image} alt="" className="w-8 h-8 object-cover border border-white/10" onError={(e) => { e.target.style.display = "none"; }} />}
            <span className="font-heading font-black text-lg">${preview.coin.symbol}</span>
            <span className="font-mono text-xs text-muted-foreground">
              MC {fmtUsd(preview.coin.marketCap)} · Liq {fmtUsd(preview.coin.liquidity)} · {Math.round(preview.coin.ageHours || 0)}h old
              {preview.coin.change24h != null && <span className={preview.coin.change24h >= 0 ? " text-neon" : " text-danger"}> · {Math.round(preview.coin.change24h)}% 24h</span>}
            </span>
          </div>

          <div className="space-y-1.5" data-testid="hype-flags">
            {preview.flags.map((f, i) => {
              const S = FLAG_STYLE[f.level] || FLAG_STYLE.warn;
              const Icon = S.icon;
              return (
                <div key={i} className={`flex items-center gap-2 border px-3 py-1.5 text-xs font-mono ${S.cls}`}>
                  <Icon size={14} weight="fill" /> {f.text}
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {drafts.map((d, i) => (
              <div key={i} className="border border-white/10 bg-black/30 p-3 flex flex-col gap-2" data-testid={`hype-variant-${i}`}>
                <span className="text-[9px] font-mono uppercase text-muted-foreground">
                  {["🔥 Degen conviction", "🧠 Smart money", "📖 Storyteller"][i] || `Variant ${i + 1}`}
                </span>
                <textarea
                  data-testid={`hype-variant-text-${i}`}
                  value={d}
                  onChange={(e) => setDrafts((ds) => ds.map((x, j) => (j === i ? e.target.value : x)))}
                  rows={7}
                  className="w-full bg-transparent border border-white/10 p-2 text-xs leading-relaxed font-mono focus:border-[#F0C000]/50 outline-none resize-y"
                />
                <div className="flex items-center justify-between">
                  <span className={`font-mono text-[10px] ${d.length > 280 ? "text-danger font-bold" : "text-muted-foreground"}`}>{d.length}/280</span>
                  <div className="flex gap-1.5">
                    <button data-testid={`hype-copy-btn-${i}`} onClick={() => { navigator.clipboard.writeText(d); toast.success("Copied"); }}
                      className="p-1.5 border border-white/15 hover:border-neon/40 hover:text-neon transition-colors"><CopyIcon size={13} /></button>
                    <button data-testid={`hype-post-btn-${i}`} onClick={() => post(i)} disabled={posting !== null || d.length > 280}
                      className="flex items-center gap-1 px-3 py-1.5 bg-neon text-black text-[11px] font-bold hover:bg-neon/90 disabled:opacity-50 transition-colors">
                      {posting === i ? <SpinnerGapIcon size={12} className="animate-spin" /> : <XLogoIcon size={12} weight="fill" />} Post
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="pt-3 border-t border-white/5" data-testid="hype-history">
          <p className="text-[10px] font-mono uppercase text-muted-foreground mb-2">Your recent calls — peak mcap since hype</p>
          <div className="flex flex-wrap gap-2">
            {history.map((h) => (
              <a key={h.tweetId} href={`https://x.com/AlphaApingrules/status/${h.tweetId}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 border border-white/12 px-2 py-1 text-[11px] font-mono hover:border-neon/40 transition-colors">
                ${(h.symbol || h.mint.slice(0, 4)).toUpperCase()}
                {h.outcomeX != null && <span className={h.outcomeX >= 2 ? "text-neon font-bold" : "text-[#F0C000]"}>peak {h.outcomeX}x</span>}
                {h.nowX != null && <span className={h.nowX >= 1 ? "text-muted-foreground" : "text-danger"}>now {h.nowX}x</span>}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
